import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StickyQuoteBar from '../../components/feature/StickyQuoteBar';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <StickyQuoteBar />

      {/* Hero Section - Changed to Teal/Cyan */}
      <section className="bg-gradient-to-r from-teal-900 via-cyan-900 to-teal-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About DSCR Loan Pros
            </h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">
              We're dedicated to helping real estate investors access flexible financing solutions. 
              Our mission is to simplify DSCR loans and connect you with the best lenders nationwide.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="text-5xl font-bold text-teal-600 mb-2">$2B+</div>
              <div className="text-slate-600 font-semibold">Loans Funded</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-5xl font-bold text-teal-600 mb-2">15K+</div>
              <div className="text-slate-600 font-semibold">Happy Investors</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="text-5xl font-bold text-teal-600 mb-2">50+</div>
              <div className="text-slate-600 font-semibold">Lending Partners</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <div className="text-5xl font-bold text-teal-600 mb-2">21 Days</div>
              <div className="text-slate-600 font-semibold">Average Close Time</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-cyan-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Our Story</h2>
              <p className="text-slate-700 mb-4 leading-relaxed">
                Founded in 2018, DSCR Loans was born from a simple observation: real estate investors 
                needed better financing options that didn't require traditional income verification.
              </p>
              <p className="text-slate-700 mb-4 leading-relaxed">
                As experienced real estate investors ourselves, we understood the frustration of being 
                denied loans despite having profitable rental properties. Traditional lenders focused on 
                W2 income instead of property cash flow.
              </p>
              <p className="text-slate-700 leading-relaxed">
                Today, we've helped thousands of investors secure DSCR loans, enabling them to grow 
                their portfolios without the hassle of income documentation. Our network of 50+ specialized 
                lenders ensures you get competitive rates and terms tailored to your investment strategy.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-96 rounded-2xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/e05b9ff56f6ced9cc95b72a668b7b1c9.jpg"
                alt="DSCR Loans Office"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Our Core Values</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-teal-200"
            >
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Transparency</h3>
              <p className="text-slate-700">
                No hidden fees, no surprises. We believe in clear communication and honest pricing 
                throughout the entire loan process.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-teal-200"
            >
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <i className="ri-speed-up-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Speed</h3>
              <p className="text-slate-700">
                Time is money in real estate investing. Our streamlined process gets you from 
                application to closing in as little as 21 days.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-teal-200"
            >
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                <i className="ri-customer-service-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Support</h3>
              <p className="text-slate-700">
                Our dedicated team of DSCR loan specialists is here to guide you every step of the way, 
                from pre-qualification to closing.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-cyan-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">Meet Our Leadership Team</h2>
          <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Experienced professionals dedicated to revolutionizing real estate investment financing
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="h-64 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20confident%20male%20business%20executive%20in%20navy%20blue%20suit%20standing%20in%20modern%20office%20corporate%20headshot%20CEO%20portrait%20with%20natural%20lighting%20and%20professional%20background%20leadership%20photography&width=400&height=500&seq=team-ceo-male&orientation=portrait"
                  alt="Michael Chen - CEO"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-1">Michael Chen</h3>
                <p className="text-teal-600 font-semibold mb-3">CEO & Founder</p>
                <p className="text-slate-600 text-sm">
                  15+ years in real estate finance. Former VP at a major national lender. 
                  Passionate about democratizing investment property financing.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="h-64 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20confident%20female%20business%20executive%20in%20elegant%20business%20attire%20standing%20in%20modern%20corporate%20office%20CFO%20portrait%20with%20natural%20lighting%20leadership%20headshot%20photography&width=400&height=500&seq=team-cfo-female&orientation=portrait"
                  alt="Sarah Martinez - CFO"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-1">Sarah Martinez</h3>
                <p className="text-teal-600 font-semibold mb-3">Chief Financial Officer</p>
                <p className="text-slate-600 text-sm">
                  CPA with 12 years in mortgage banking. Expert in structuring complex 
                  investment property loans and risk management.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
            >
              <div className="h-64 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20friendly%20male%20business%20executive%20in%20business%20casual%20attire%20smiling%20in%20modern%20office%20environment%20COO%20portrait%20approachable%20leadership%20headshot%20with%20natural%20lighting&width=400&height=500&seq=team-coo-male&orientation=portrait"
                  alt="David Thompson - COO"
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-1">David Thompson</h3>
                <p className="text-teal-600 font-semibold mb-3">Chief Operating Officer</p>
                <p className="text-slate-600 text-sm">
                  Operations specialist with 10+ years optimizing loan processes. 
                  Focused on delivering exceptional customer experiences.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section - Changed to Teal/Cyan */}
      <section className="py-16 bg-gradient-to-r from-teal-900 via-cyan-900 to-teal-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-xl text-teal-100 mb-8">
            Join thousands of successful real estate investors who trust us for their DSCR loan needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/apply"
              className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 px-8 py-4 rounded-lg font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-amber-500/50 hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Apply Now
            </Link>
            <Link
              to="/dscr-loans"
              className="inline-flex items-center justify-center bg-white/10 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap"
            >
              <i className="ri-information-line mr-2"></i>
              Learn More
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}